import React, { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Button, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";

import Breadcrumbs from "../../common2/Breadcrumbs";
import CommonStyles from "../../common/CommonStyles";
import CustomTheme from "../../common/CustomTheme";

import { IState } from "../../../redux/reducers/rootReducers";
import { getAzureUserDetails } from "../../../redux/actions/userManagement/azureUsers";

type ReadonlyFieldProps = {
  title: string;
  value: any;
  classes: any;
  fieldBg: string;
  fieldText: string;
  fieldBorder: string;
};

const ReadonlyField = ({
  title,
  value,
  classes,
  fieldBg,
  fieldText,
  fieldBorder,
}: ReadonlyFieldProps) => {
  const displayValue =
    value === null || value === undefined || value === "" ? "------" : String(value);

  return (
    <Grid item md={4} xs={12}>
      <Box
        sx={{
          paddingLeft: "20px",
          paddingBottom: "4px",
          width: "calc(100% - 40px)",
          minHeight: "44px",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <Typography
          className={classes.BoxTitle}
          sx={{
            display: "block",
            whiteSpace: "nowrap",
            lineHeight: "20px",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          marginLeft: "20px",
          marginRight: "20px",
          minHeight: "40px",
          borderRadius: "4px",
          border: `1px solid ${fieldBorder}`,
          backgroundColor: fieldBg,
          color: fieldText,
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          overflow: "hidden",
        }}
      >
        <Tooltip title={displayValue} arrow placement="top-start">
          <Typography
            variant="body2"
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayValue}
          </Typography>
        </Tooltip>
      </Box>
    </Grid>
  );
};

const AzureUserDetails = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { id } = useParams();

  const classes = CommonStyles();

  const detailsState = useSelector((state: IState) => state?.azureUserReducer?.UserDetails);
  const toggleTheme = useSelector((state: IState) => state.theme.theme);

  const loading = detailsState?.loading || false;
  const detailsError = detailsState?.error || "";
  const user = detailsState?.data?.user || null;

  const themeData = CustomTheme();
  const mode = toggleTheme ? themeData.all.dark : themeData.all.light;
  const isDark = !!toggleTheme;

  const fieldBg = isDark ? mode?.body || "#2a2d35" : "#ffffff";
  const fieldText = isDark ? mode?.text || "#ffffff" : "#000000";
  const fieldBorder = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.16)";

  useEffect(() => {
    if (id) {
      dispatch(getAzureUserDetails(id));
    }
  }, [dispatch, id]);

  const isCorrectUserLoaded = useMemo(() => {
    if (!id) return false;
    if (!user?.id) return false;
    return String(user.id).toLowerCase() === String(id).toLowerCase();
  }, [user, id]);

  const showLoader = loading || !isCorrectUserLoaded;

  const backHandler = () => {
    navigate("/AzureManagement/AzureUsers");
  };

  const accountStatus =
    user?.accountEnabled === true
      ? "Active"
      : user?.accountEnabled === false
      ? "Inactive"
      : "------";

  if (showLoader && !detailsError) {
    return (
      <>
        <Breadcrumbs
          data={[
            { title: "Contact Management ", path: "/ContactManagement" },
            { title: "Azure Users ", path: "/AzureManagement/AzureUsers" },
            { title: "Details ", path: `/AzureManagement/AzureUsers/${id}` },
          ]}
        />

        <Box className={`pageHolder ${classes.px1}`}>
          <Box className="mb2 pb2">
            <Typography variant="h6" className={`${classes.themeColorTxt}`}>
              View Azure User
            </Typography>
          </Box>

          <Box
            sx={{
              minHeight: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs
        data={[
          { title: "Contact Management ", path: "/ContactManagement" },
          { title: "Azure Users ", path: "/AzureManagement/AzureUsers" },
          { title: "Details ", path: `/AzureManagement/AzureUsers/${id}` },
        ]}
      />

      <Box className={`pageHolder ${classes.px1}`}>
        <Box className="mb2 pb2">
          <Typography variant="h6" className={`${classes.themeColorTxt}`}>
            View Azure User
          </Typography>
        </Box>

        {detailsError ? (
          <Box className="mb1 px-25">
            <Typography variant="body2" color="error">
              {String(detailsError)}
            </Typography>
          </Box>
        ) : null}

        <Grid container spacing={1}>
          <ReadonlyField
            title="Name"
            value={user?.displayName}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="User Principal Name"
            value={user?.userPrincipalName}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Mail Nickname"
            value={user?.mailNickname}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="First Name"
            value={user?.givenName}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Last Name"
            value={user?.surname}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Job Title"
            value={user?.jobTitle}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Mobile No"
            value={user?.mobilePhone}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Office Location"
            value={user?.officeLocation}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Usage Location"
            value={user?.usageLocation}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Alternate Email"
            value={user?.otherMails?.[0]}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Status"
            value={accountStatus}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />

          <ReadonlyField
            title="Email"
            value={user?.mail}
            classes={classes}
            fieldBg={fieldBg}
            fieldText={fieldText}
            fieldBorder={fieldBorder}
          />
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box className="text-center" sx={{ marginTop: "10px" }}>
              <Button
                className={`${classes.btn} ${classes.btnCancel}`}
                variant="outlined"
                onClick={backHandler}
              >
                <CancelIcon style={{ marginRight: "5px" }} />
                Back
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AzureUserDetails;